import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  parsePhoneNumberFromString,
  isSupportedCountry,
  getCountries,
  getCountryCallingCode,
  AsYouType
} from 'libphonenumber-js/max';
import './PhoneField.css';

const PHONE_TYPE_MAP = {
  MOBILE: 'MOBILE',
  FIXED_LINE: 'FIXED_LINE',
  VOIP: 'VOIP'
};

const normalizeCountry = (country) => {
  if (!country || typeof country !== 'string') return null;
  const upper = country.toUpperCase();
  if (!isSupportedCountry(upper)) return null;
  return upper;
};

const getPhoneType = (phoneNumber) => {
  try {
    if (!phoneNumber || typeof phoneNumber.getType !== 'function') return 'UNKNOWN';
    const detectedType = phoneNumber.getType();
    return PHONE_TYPE_MAP[detectedType] || 'UNKNOWN';
  } catch (error) {
    return 'UNKNOWN';
  }
};

/** Normalize raw input for parsing: 00... -> +... */
const normalizeRawForParse = (raw) => {
  if (typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  if (trimmed.startsWith('00')) return '+' + trimmed.slice(2);
  return trimmed;
};

const formatAsYouType = (rawValue, countryValue) => {
  const normalized = normalizeRawForParse(rawValue);
  if (!normalized) return '';

  try {
    const formatter = new AsYouType(countryValue || undefined);
    const formatted = formatter.input(normalized);
    return formatted || normalized;
  } catch (error) {
    return normalized;
  }
};

const buildPayload = ({ rawInputValue, countryValue }) => {
  const rawInput = typeof rawInputValue === 'string' ? rawInputValue : '';
  const toParse = normalizeRawForParse(rawInput);

  let parsed = null;
  if (toParse) {
    parsed = parsePhoneNumberFromString(toParse, countryValue || undefined);
  }

  const e164 = parsed?.number ?? null;
  const isPossible = parsed ? parsed.isPossible() : false;
  const isValid = parsed ? parsed.isValid() : false;

  return {
    e164,
    countryIso2: parsed?.country ?? countryValue ?? null,
    national: parsed?.nationalNumber ?? null,
    isPossible,
    isValid,
    type: getPhoneType(parsed),
    rawInput
  };
};

const COUNTRY_LIST = getCountries().filter((c) => isSupportedCountry(c));

const PhoneField = ({
  valueE164,
  defaultCountry = 'AR',
  onChange,
  onBlur,
  required = false,
  disabled = false,
  error = '',
  name = 'phone',
  id = 'phone',
  label = 'Teléfono',
  placeholder = '',
  helperText = '',
  autoFocus = false,
  showE164Helper = true
}) => {
  const hasExternalValue = typeof valueE164 !== 'undefined';
  const [countryIso2, setCountryIso2] = useState(
    () => normalizeCountry(defaultCountry) || 'AR'
  );
  const [displayValue, setDisplayValue] = useState('');

  useEffect(() => {
    const normalized = normalizeCountry(defaultCountry);
    if (normalized) setCountryIso2(normalized);
  }, [defaultCountry]);

  useEffect(() => {
    if (!hasExternalValue || !valueE164) return;
    const parsed = parsePhoneNumberFromString(valueE164);
    if (parsed) {
      setCountryIso2(parsed.country || countryIso2);
      setDisplayValue(
        formatAsYouType(parsed.nationalNumber || parsed.number || '', parsed.country || countryIso2)
      );
    }
  }, [valueE164, hasExternalValue]);

  const payload = useMemo(
    () =>
      buildPayload({
        rawInputValue: displayValue,
        countryValue: countryIso2
      }),
    [displayValue, countryIso2]
  );

  const handleInputChange = (e) => {
    const next = e.target.value ?? '';
    const formattedValue = formatAsYouType(next, countryIso2);
    setDisplayValue(formattedValue);
    const nextPayload = buildPayload({
      rawInputValue: formattedValue,
      countryValue: countryIso2
    });
    if (onChange) onChange(nextPayload);
  };

  const handleCountryChange = (e) => {
    const nextCountry = e.target.value || null;
    const country = normalizeCountry(nextCountry) || countryIso2;
    setCountryIso2(country);
    const reformattedValue = formatAsYouType(displayValue, country);
    setDisplayValue(reformattedValue);
    const nextPayload = buildPayload({
      rawInputValue: reformattedValue,
      countryValue: country
    });
    if (onChange) onChange(nextPayload);
  };

  const handleBlur = () => {
    const formattedOnBlur = formatAsYouType(displayValue, countryIso2);
    if (formattedOnBlur !== displayValue) {
      setDisplayValue(formattedOnBlur);
    }
    const finalPayload = buildPayload({
      rawInputValue: formattedOnBlur,
      countryValue: countryIso2
    });
    if (onBlur) onBlur(finalPayload);
  };

  return (
    <div className={`rg-phone-field ${error ? 'rg-phone-field--invalid' : ''}`}>
      {label ? (
        <label htmlFor={id} className="form-label">
          {label}
        </label>
      ) : null}

      <div className="rg-phone-field__control">
        <div className="PhoneInput rg-phone-field__input">
          <div className="PhoneInputCountry">
            <select
              aria-label="Seleccionar país"
              title="Seleccionar país"
              className="PhoneInputCountrySelect"
              value={countryIso2 || ''}
              onChange={handleCountryChange}
              disabled={disabled}
            >
              {COUNTRY_LIST.map((code) => (
                <option key={code} value={code}>
                  +{getCountryCallingCode(code)} {code}
                </option>
              ))}
            </select>
          </div>
          <input
            id={id}
            name={name}
            type="tel"
            className="PhoneInputInput"
            value={displayValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            autoComplete="tel"
            autoFocus={autoFocus}
            aria-invalid={Boolean(error)}
            aria-describedby={error || helperText ? `${id}-feedback` : undefined}
          />
        </div>
      </div>

      {error ? (
        <div id={`${id}-feedback`} className="invalid-feedback d-block">
          {error}
        </div>
      ) : null}

      {!error && helperText ? (
        <small id={helperText ? `${id}-feedback` : undefined} className="form-text text-muted d-block mt-1">
          {helperText}
        </small>
      ) : null}

      {!error && showE164Helper && payload.e164 ? (
        <small className="form-text text-muted d-block mt-1">
          Te enviaremos a: {payload.e164}
        </small>
      ) : null}
    </div>
  );
};

PhoneField.propTypes = {
  valueE164: PropTypes.string,
  defaultCountry: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  name: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  helperText: PropTypes.string,
  autoFocus: PropTypes.bool,
  showE164Helper: PropTypes.bool
};

export default PhoneField;
